"use client";

import { SignInButton, useAuth } from "@clerk/nextjs";
import { SignedIn, SignedOut } from "modules/auth/components/ClientAuthGate";
import React from "react";
import { MessageCircle, Send } from "lucide-react";
import { TradeItem } from "modules/tradeLists/model/tradeList";
import { useRouter } from "@bprogress/next/app";
import { useChatMessages } from "modules/chat/hooks/useChatMessages";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";

interface ContactUserButtonProps {
  userId: string;
  tradeItem?: TradeItem;
  /** Wrapper classes; defaults to the trade-card spacing. */
  className?: string;
}

function ContactUserButton({ userId, className }: ContactUserButtonProps) {
  const router = useRouter();
  const { userId: currentUserId, isSignedIn, isLoaded } = useAuth();

  const isOtherUser = isLoaded && userId !== currentUserId;

  const { getOrCreateChat } = useChatMessages();
  const isPending = getOrCreateChat.isPending;

  const handleCreateChat = async () => {
    if (!isSignedIn || !userId) return;

    try {
      const chat = await getOrCreateChat.mutateAsync([currentUserId!, userId]);
      router.push(`/x/inbox/${chat.id}`);
    } catch {
      // Error toast is handled by useChatMessages onError callback
    }
  };

  // Neutral placeholder until Clerk resolves — otherwise signed-in users see
  // the "Sign In to Message" CTA flash before it flips to "Message". Mirrors the
  // real btn-sm box (height, padding, transparent icon+label give it the same
  // intrinsic width) so it can't collapse in an auto-width row or reflow on load.
  if (!isLoaded) {
    return (
      <div className={className ?? "mt-2"}>
        <div className="skeleton rounded-field flex h-8 w-full items-center justify-center gap-2 px-3 text-sm font-medium text-transparent">
          <MessageCircle size={ICON_SIZE} />
          Message
        </div>
      </div>
    );
  }

  return (
    <div className={className ?? "mt-2"}>
      <SignedIn>
        {isOtherUser ? (
          <button
            onClick={handleCreateChat}
            disabled={isPending}
            className="btn btn-primary btn-sm w-full"
          >
            {isPending ? (
              <>
                <span className="loading loading-spinner loading-xs"></span>
                Creating...
              </>
            ) : (
              <>
                <MessageCircle size={ICON_SIZE} />
                Message
              </>
            )}
          </button>
        ) : null}
      </SignedIn>
      <SignedOut>
        <SignInButton mode="modal">
          <button className="btn btn-primary btn-sm w-full">
            <MessageCircle size={ICON_SIZE} />
            Message
          </button>
        </SignInButton>
      </SignedOut>
    </div>
  );
}

export default ContactUserButton;
